import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NotificationsPopup from '../NotificationsPopup';
import {
  useMediaQuery,
  Badge,
  Grid,
  Toolbar,
  createTheme,
  Collapse,
  Paper,
  Chip,
  Menu,
  MenuItem as MuiMenuItem,
  Typography,
} from '@mui/material';
import TaskIcon from '@mui/icons-material/Task';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Keys } from '../../enums/localStorage.js';
import { getOrgData } from '../../organization';
import GetValidatedTokenData from '../../utils/helper';
import { ThemeProvider } from '@emotion/react';
import fallbackAvatar from '../../assets/SidebarResponsive/account-group-outline.svg';
import { organizationAPI } from '../../api/requests/organization/organizationAPI';
import { getCachedOrgRole, setCachedOrgRole } from '../../utils/orgRoleCache';
import {
  ChatBubbleOutline,
  ExpandLess,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { ChatApi } from '../../api/requests/chat/ChatApi';
import sideBarLogo from '../../assets/Logo/side_bar_logo.svg';
import sideBarLogoDark from '../../assets/Logo/logo_dark.svg';
import navbar_logo from '../../assets/Logo/navbar_logo.svg';
import mini_sideBar_logo from '../../assets/Logo/mini_sideBar_logo.svg';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Skeletons from '../Skeleton/Skeletons';
import useSafeNavigate from '../../hooks/useSafeNavigate.js';
import imageConfig from '../../assets/imageConfig.js';
import { imageHandler } from '../../utils/imageHandler.js';
import { LuTrophy } from 'react-icons/lu';
import { userAuthAPI } from '../../api/requests/users/userAuthAPI.jsx';
import handleSignOut from '../../utils/handleSignOut.js';
import { useChatStore, useChatStoreHandlers } from '../../Redux/Slices/chatSlice.js';

import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import { useAppTheme } from '../../theme/ThemeProvider/AppTheme/Provider.jsx';
import ShowWhen from '../Others/ShowWhen.jsx';

export const DRAWER_WIDTH = 200;
export const MINI_DRAWER_WIDTH = 60;

const MenuItem = ({ item, selected, onClick, isCollapsed, isSubItem = false }) => (
  <ListItemButton
    onClick={onClick}
    sx={{
      fontSize: '13px',
      height: isSubItem ? '36px' : '42px',
      borderRadius: '8px',
      mb: 0.5,
      mx: 1,
      pl: isCollapsed ? 1.25 : 2.5,
      pr: 1,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: selected ? 'var(--color-primary)' : 'transparent',
      color: selected ? 'white' : 'var(--color-text-secondary)',
      justifyContent: isCollapsed ? 'center' : 'initial',
      '&:hover': {
        backgroundColor: selected ? 'var(--color-primary)' : '',
        color: 'white',
        // boxShadow: selected ? '0 4px 12px rgba(37, 99, 235, 0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
      },
      // boxShadow: selected ? '0 2px 8px rgba(37, 99, 235, 0.25)' : 'none',
      position: 'relative',
      overflow: 'hidden',
      '&::before': selected
        ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(37,99,235,0.08)',
          pointerEvents: 'none',
        }
        : {},
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: isCollapsed ? '24px' : '32px',
        width: '24px',
        height: '24px',
        color: selected ? 'rgba(255,255,255,0.9)' : 'var(--color-text-secondary)',
        transition: 'color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {item.icon}
    </ListItemIcon>
    {!isCollapsed && (
      <ListItemText
        primary={item.name.length > 14 ? item.name.substring(0, 14) + '...' : item.name}
        primaryTypographyProps={{
          fontSize: isSubItem ? '12px' : '13px',
          fontWeight: selected ? '600' : '500',
          color: selected ? 'white' : 'var(--color-text-secondary)',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        }}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          opacity: isCollapsed ? 0 : 1,
          transform: isCollapsed ? 'translateX(-20px)' : 'translateX(0)',
          transition: 'opacity 0.2s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isCollapsed ? 'none' : 'auto',
          display: isCollapsed ? 'none' : 'block',
          '.MuiDrawer-paper:hover &': {
            opacity: 1,
            transform: 'translateX(0)',
            display: 'block',
          },
        }}
      />
    )}
  </ListItemButton>
);

function SideBarResponsive(props) {
  const { theme, resolvedTheme, setTheme } = useAppTheme();

  const params = useParams();
  const orgName = params.orgName;
  const [domainData, setDomainData] = useState(null);

  const {
    [imageConfig.sideBar['logo']]: side_bar_logo_url,
    [imageConfig.navbar['logo']]: home_navbar_phone_url,
  } = imageHandler.getAll();

  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [themeAnchor, setThemeAnchor] = useState(null);
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const isPreview = query.get('preview') === 'true';

  const [loading, setLoading] = useState(!isPreview);

  let showAppBar = null;
  const isContestPage = location.pathname === '/contest';
  let isCoursePage =
    location.pathname.includes('/course/videos') ||
    (location.pathname.includes('/course/upload') && props.show !== true) ||
    (location.pathname.includes('/course/create') && props.show !== true) ||
    (location.pathname.includes('course/update') && props.show !== true);
  let isChatPage = location.pathname.includes('/chat');

  if (location.pathname.includes('/contest') && location.pathname.includes('/rankings')) {
    showAppBar = false;
  } else if (
    location.pathname.includes('course/videos') ||
    location.pathname.includes('/course/upload') ||
    location.pathname.includes('/course/create') ||
    location.pathname.includes('/course/update')
  ) {
    showAppBar = false;
  }
  const [userNotificationLength, setUserNotificationLength] = useState(0);
  const [inviteNotificationNumber, setInviteNotificationNumber] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const { unreadCount } = useChatStore('unreadCount');
  const { unreadCountHandler } = useChatStoreHandlers('unreadCount');

  const fetchUnreadChatMessageCount = async () => {
    try {
      const { count, unreadUserChats, unreadGroupChats } = await ChatApi.getUnreadCount();

      unreadCountHandler.set({
        total: count,
        user: unreadUserChats,
        group: unreadGroupChats,
      });
    } catch (error) {
      unreadCountHandler.set({
        total: 0,
        user: 0,
        group: 0,
      });
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await organizationAPI.getNotifications();
      setNotificationCount(response?.unreadCount ?? 0);
    } catch (error) {
      setNotificationCount(0);
    }
  };

  const fetchInvite = async () => {
    try {
      const response = await organizationAPI.getInvitesRecievedByUser();
      setInviteNotificationNumber(response.length);
    } catch (error) {}
  };

  useEffect(() => {
    if (isPreview) return;

    fetchInvite();
    fetchNotifications();
    fetchUnreadChatMessageCount();
  }, []);

  useEffect(() => {
    if (isPreview) return;
    setNotificationCount(userNotificationLength + inviteNotificationNumber);
  }, [userNotificationLength, inviteNotificationNumber]);

  const [userRole, setUserRole] = useState('');
  const [orgLogoLink, setOrgLogoLink] = useState('');

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('Token');

      if (!token) {
        return;
      }

      // const isPublicPage = window.location.pathname.startsWith('/p/');
      // if (isPublicPage) {
      //   return;
      // }

      if (orgName) {
        // Check cache first to prevent duplicate API calls
        const cached = getCachedOrgRole(orgName);

        if (cached) {
          setUserRole(cached.role);
          setOrgLogoLink(cached.logoLink);
          return;
        }

        const response = await organizationAPI.getOrgRole(orgName);

        if (response) {
          setCachedOrgRole(orgName, response);
        }

        setUserRole(response.role);
        setOrgLogoLink(response.logoLink);
      }
    } catch (error) {
      console.error('User role not available (user may not be a member):', error.message);
    }
  };

  const [tokenData, setTokenData] = React.useState(null);

  const checkToken = () => {
    const token = GetValidatedTokenData();
    if (token) {
      setTokenData(token);
    }
  };

  useEffect(() => {
    checkToken();
    if (isPreview) return;
    getDomainData();
  }, []);

  const getDomainData = async () => {
    const data = await getOrgData();
    setDomainData(data);
    setLoading(false);
  };
  const navigate = useSafeNavigate();

  const [selectedPath, setSelectedPath] = useState(`org/${orgName}/dashboard`);
  useEffect(() => {
    if (isPreview) return;
    const currentPath = location.pathname;
    if (selectedPath === currentPath) {
      setSelectedPath('');
    } else {
      setSelectedPath(currentPath);
    }
  }, [location.pathname]);
  const handleNavigate = (e, path) => {
    e.preventDefault();
    navigate(`${path}`);
    if (isMobileView) {
      setMobileOpen(false);
    }
  };
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setDrawerOpen(!mobileOpen);
  };
  const handleLogout = () => {
    handleSignOut();
    if (isMobileView) {
      setMobileOpen(false);
    }
  };
  const [isMobileView, setIsMobileView] = useState(false);

  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    setIsMobileView(isMobile);
    fetchUserRole();
  }, [isMobile, orgName]);

  const [notificationsPopup, setNotificationsPopup] = useState({
    open: false,
    anchorEl: null,
    placement: '',
  });
  const toggleNotificationsPopup = (event) => {
    const isOpening = !notificationsPopup.open;
    if (!isOpening) {
      fetchNotifications();
      fetchInvite();
    }

    setNotificationsPopup((prev) => {
      if (prev.open) {
        return {
          open: false,
          anchorEl: null,
          placement: '',
        };
      } else {
        return {
          open: true,
          anchorEl: event.currentTarget,
          placement: isMobileView ? 'top' : 'right',
        };
      }
    });

    if (isMobileView) {
      setMobileOpen(false);
    }
  };

  const closeNotificationsPopup = () => {
    setNotificationsPopup({
      open: false,
      anchorEl: null,
      placement: '',
    });
    fetchNotifications();
    fetchInvite();
  };

  const handleDrawerHover = (isHovering) => {
    if (isCoursePage && !isMobileView) {
      setDrawerOpen(isHovering || mobileOpen);
    }
  };

  useEffect(() => {
    if (isPreview) return;
    if (isCoursePage && !isMobileView) {
      setDrawerOpen(false);
    }
  }, [location.pathname, isCoursePage, isMobileView]);

  const renderOrganizationImage = () => {
    if (orgLogoLink) {
      return (
        <Box
          sx={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-bg-secondary)',
            boxShadow: '0 2px 8px var(--color-shadow)',
            p: 0,
          }}
        >
          <img
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              objectFit: 'cover',
              display: 'block',
              margin: '0 auto',
            }}
            src={orgLogoLink}
            alt=""
          />
        </Box>
      );
    } else {
      const orgNameInitial = orgName?.charAt(0) || '';

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '26px',
            height: '26px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '50%',
            textAlign: 'center',
            textTransform: 'uppercase',
            boxShadow: '0 2px 8px var(--color-shadow)',
            p: 0,
          }}
        >
          <span
            style={{
              display: 'flex',
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            {orgNameInitial}
          </span>
        </Box>
      );
    }
  };

  const [expandedSections, setExpandedSections] = useState({
    myActivities: false,
    manage: false,
    admin: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getDashboardItem = () => {
    return {
      name: orgName,
      path: `/org/${orgName}/dashboard`,
      icon: renderOrganizationImage(),
    };
  };

  const getMyActivitiesItems = () => {
    return [
      {
        name: 'Courses',
        path: `/org/${orgName}/course`,
        icon: <ImportContactsOutlinedIcon sx={{ fontSize: '20px', color: 'inherit!important' }} />,
      },
      {
        name: 'Contests',
        path: `/org/${orgName}/contest`,
        icon: <EmojiEventsIcon sx={{ fontSize: '20px', color: 'inherit!important' }} />,
      },
      {
        name: 'Tasks',
        path: `/org/${orgName}/task/assigned`,
        icon: <FormatListBulletedIcon sx={{ fontSize: '20px' }} />,
      },
      {
        name: 'Time Table',
        path: `/org/${orgName}/timetable/my`,
        icon: <EditCalendarIcon sx={{ fontSize: '20px', color: 'inherit!important' }} />,
      },
      {
        name: 'Results',
        path: `/org/${orgName}/results/my-results`,
        icon: <AssessmentIcon sx={{ fontSize: '20px', color: 'inherit!important' }} />,
      },
    ];
  };

  const getChatItem = () => {
    const chatIcon = <ChatBubbleOutline sx={{ fontSize: '20px' }} />;
    return {
      name: 'Chat',
      path: `/org/${orgName}/chat`,
      icon: (
        <Badge
          badgeContent={unreadCount.total}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '10px',
              height: '16px',
              minWidth: '16px',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              border: '1px solid white',
            },
          }}
        >
          <ChatBubbleOutline sx={{ fontSize: '20px' }} />
        </Badge>
      ),
    };
  };

  const getMeetItem = () => {
    return {
      name: 'Meet',
      path: `/org/${orgName}/meet`,
      icon: <VideocamOutlined sx={{ fontSize: '20px' }} />,
    };
  };

  const getResourcesItem = () => {
    return {
      name: 'Resources',
      path: `/org/${orgName}/doc/file`,
      icon: <FolderOutlinedIcon sx={{ fontSize: '20px' }} />,
    };
  };

  const getYourOrgsItem = () => {
    return {
      name: 'Your Orgs',
      path: '/organization',
      icon: <CorporateFareIcon sx={{ fontSize: '20px' }} />,
    };
  };

  const getManageItems = () => {
    if (userRole !== 'ADMIN' && userRole !== 'SUPERADMIN' && userRole !== 'CREATOR') {
      return [];
    }

    return [
      {
        name: 'Course',
        path: `/org/${orgName}/course/user`,
        icon: <ImportContactsOutlinedIcon sx={{ fontSize: '20px' }} />,
      },
      {
        name: 'Time Table',
        path: `/org/${orgName}/timetable/template-listing`,
        icon: <EditCalendarIcon sx={{ fontSize: '20px' }} />,
      },
      {
        name: 'Contest',
        path: `/org/${orgName}/contest/user`,
        icon: <LuTrophy style={{ fontSize: '20px' }} />,
      },
      {
        name: 'Task',
        path: `/org/${orgName}/task/created`,
        icon: <TaskIcon sx={{ fontSize: '20px' }} />,
      },
      {
        name: 'Results',
        path: `/org/${orgName}/results`,
        icon: <AssessmentIcon sx={{ fontSize: '20px' }} />,
      },
    ];
  };

  const getAdminItems = () => {
    if (userRole !== 'SUPERADMIN' && userRole !== 'CREATOR') {
      return [];
    }

    return [
      {
        name: 'Settings',
        path: `/org/${orgName}/settings`,
        icon: <SettingsOutlinedIcon sx={{ fontSize: '20px' }} />,
      },
      {
        name: 'Member Report',
        path: `/org/${orgName}/member/report`,
        icon: <AssessmentIcon sx={{ fontSize: '20px' }} />,
      },
    ];
  };
  let pagesBottom = [
    {
      name: 'User Profile',
      path: '/user-profile',
      icon: <AccountCircleOutlinedIcon sx={{ fontSize: '20px' }} />,
    },
    {
      name: 'Notifications',
      onClick: toggleNotificationsPopup,
      icon: (
        <Badge
          badgeContent={notificationCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '10px',
              height: '16px',
              minWidth: '16px',
              backgroundColor: 'var(--color-error)',
              color: 'white',
            },
          }}
        >
          <NotificationsNoneIcon sx={{ fontSize: '20px' }} />
        </Badge>
      ),
    },
    {
      name: theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light',
      onClick: (e) => setThemeAnchor(e.currentTarget),
      icon: theme === 'system' ? <SettingsBrightnessOutlinedIcon sx={{ fontSize: '20px' }} /> : theme === 'dark' ? <DarkModeOutlinedIcon sx={{ fontSize: '20px' }} /> : <LightModeOutlinedIcon sx={{ fontSize: '20px' }} />,
    },
    {
      name: 'Logout',
      onClick: handleLogout,
      icon: <LogoutIcon sx={{ fontSize: '20px' }} />,
    },
  ];
  const sidebarUserProfileTop = [
    {
      name: 'Your Orgs',
      path: '/organization?filter=joined',
      icon: <CorporateFareIcon sx={{ fontSize: '20px' }} />,
    },
  ];
  const sidebarUserProfileBottom = [
    {
      name: 'User Profile',
      path: '/user-profile',
      icon: <AccountCircleOutlinedIcon sx={{ fontSize: '20px' }} />,
    },
    {
      name: 'Notifications',
      onClick: toggleNotificationsPopup,
      icon: (
        <Badge
          badgeContent={notificationCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '10px',
              height: '16px',
              minWidth: '16px',
              backgroundColor: 'var(--color-error)',
              color: 'white',
            },
          }}
        >
          <NotificationsNoneIcon sx={{ fontSize: '20px', color: 'var(--color-text-secondary)' }} />
        </Badge>
      ),
    },
    {
      name: 'Logout',
      onClick: handleLogout,
      icon: <LogoutIcon sx={{ fontSize: '20px' }} />,
    },
  ];

  const SectionHeader = ({ title, icon, expanded, onClick, isCollapsed }) => (
    <ListItemButton
      onClick={onClick}
      sx={{
        fontSize: '13px',
        height: '42px',
        borderRadius: '8px',
        mb: 0.5,
        mx: 1,
        pl: isCollapsed ? 1.25 : 1.5,
        pr: 1,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: 'rgba(37, 99, 235, 0.08)',
        },
        backgroundColor: expanded ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
        border: expanded ? '1px solid rgba(37, 99, 235, 0.1)' : '1px solid transparent',
        justifyContent: isCollapsed ? 'center' : 'initial',
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? '24px' : '32px',
          width: '24px',
          height: '24px',
          color: expanded ? 'var(--color-primary)' : '#64748b',
          transition: 'color 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {icon}
        {isCollapsed && expanded && (
          <Box
            sx={{
              position: 'absolute',
              fontSize: '16px',
              color: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: '16px' }} />
          </Box>
        )}
      </ListItemIcon>
      {!isCollapsed && (
        <React.Fragment>
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: expanded ? '600' : '500',
              color: expanded ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}
            sx={{
              opacity: isCollapsed ? 0 : 1,
              transform: isCollapsed ? 'translateX(-20px)' : 'translateX(0)',
              transition: 'opacity 0.2s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: isCollapsed ? 'none' : 'auto',
              display: isCollapsed ? 'none' : 'block',
              '.MuiDrawer-paper:hover &': {
                opacity: 1,
                transform: 'translateX(0)',
                display: 'block',
              },
            }}
          />
          <Box
            sx={{
              color: expanded ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              transition: 'all 0.2s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              opacity: isCollapsed ? 0 : 1,
              display: isCollapsed ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '.MuiDrawer-paper:hover &': {
                opacity: 1,
              },
            }}
          >
            <KeyboardArrowRight sx={{ fontSize: '16px' }} />
          </Box>
        </React.Fragment>
      )}
    </ListItemButton>
  );

  const renderMenuSections = () => {
    const dashboardItem = getDashboardItem();
    const myActivitiesItems = getMyActivitiesItems();
    const chatItem = getChatItem();
    const meetItem = getMeetItem();
    const resourcesItem = getResourcesItem();
    const yourOrgsItem = getYourOrgsItem();
    const manageItems = getManageItems();
    const adminItems = getAdminItems();
    const isCollapsed = isCoursePage && !isMobileView && !drawerOpen;

    return (
      <Box sx={{ px: 0, py: 0.5 }}>
        <MenuItem
          item={dashboardItem}
          selected={selectedPath === dashboardItem.path}
          onClick={(e) => handleNavigate(e, dashboardItem.path)}
          isCollapsed={isCollapsed}
        />
        {myActivitiesItems.length > 0 && (
          <Box
            sx={{
              mb: 0.5,
              backgroundColor: expandedSections.myActivities
                ? 'rgba(99, 102, 241, 0.03)'
                : 'transparent',
              borderRadius: isCollapsed ? '20px' : '8px',
              mx: isCollapsed ? 0.5 : 0,
              mt: isCollapsed ? 1 : 0.5,
            }}
          >
            <SectionHeader
              title="My Activities"
              icon={<TaskAltIcon sx={{ fontSize: '20px' }} />}
              expanded={expandedSections.myActivities}
              onClick={() => toggleSection('myActivities')}
              isCollapsed={isCollapsed}
            />
            <Collapse in={expandedSections.myActivities} timeout={200}>
              <Box sx={{ mt: 0 }}>
                {myActivitiesItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    item={item}
                    selected={selectedPath === item.path}
                    onClick={(e) => handleNavigate(e, item.path)}
                    isCollapsed={isCollapsed}
                    isSubItem={true}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
        <MenuItem
          item={chatItem}
          selected={selectedPath === chatItem.path}
          onClick={(e) => handleNavigate(e, chatItem.path)}
          isCollapsed={isCollapsed}
        />
        <MenuItem
          item={meetItem}
          selected={selectedPath === meetItem.path}
          onClick={(e) => handleNavigate(e, meetItem.path)}
          isCollapsed={isCollapsed}
        />
        <MenuItem
          item={resourcesItem}
          selected={selectedPath === resourcesItem.path}
          onClick={(e) => handleNavigate(e, resourcesItem.path)}
          isCollapsed={isCollapsed}
        />
        <MenuItem
          item={yourOrgsItem}
          selected={selectedPath === yourOrgsItem.path}
          onClick={(e) => handleNavigate(e, yourOrgsItem.path)}
          isCollapsed={isCollapsed}
        />
        {manageItems.length > 0 && (
          <Box
            sx={{
              mb: 0.5,
              mt: 0.5,
              backgroundColor: expandedSections.manage ? 'rgba(99, 102, 241, 0.03)' : 'transparent',
              borderRadius: isCollapsed ? '20px' : '8px',
              mx: isCollapsed ? 0.5 : 0,
            }}
          >
            <SectionHeader
              title="Manage"
              icon={<DashboardIcon sx={{ fontSize: '20px' }} />}
              expanded={expandedSections.manage}
              onClick={() => toggleSection('manage')}
              isCollapsed={isCollapsed}
            />
            <Collapse in={expandedSections.manage} timeout={200}>
              <Box sx={{ mt: 0 }}>
                {manageItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    item={item}
                    selected={selectedPath === item.path}
                    onClick={(e) => handleNavigate(e, item.path)}
                    isCollapsed={isCollapsed}
                    isSubItem={true}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
        {adminItems.length > 0 && (
          <Box
            sx={{
              mb: 0.5,
              backgroundColor: expandedSections.admin ? 'rgba(99, 102, 241, 0.03)' : 'transparent',
              borderRadius: isCollapsed ? '20px' : '8px',
              mx: isCollapsed ? 0.5 : 0,
            }}
          >
            <SectionHeader
              title="Admin"
              icon={<AdminPanelSettingsIcon sx={{ fontSize: '20px' }} />}
              expanded={expandedSections.admin}
              onClick={() => toggleSection('admin')}
              isCollapsed={isCollapsed}
            />
            <Collapse in={expandedSections.admin} timeout={200}>
              <Box sx={{ mt: 0 }}>
                {adminItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    item={item}
                    selected={selectedPath === item.path}
                    onClick={(e) => handleNavigate(e, item.path)}
                    isCollapsed={isCollapsed}
                    isSubItem={true}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    );
  };

  const sidebarTheme = createTheme({
    components: {
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '32px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minWidth: '24px',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            '.MuiAvatar-fallback': {
              background: `url(${fallbackAvatar}) center no-repeat`,
            },
          },
        },
      },
      MuiListItemAvatar: {
        styleOverrides: {
          root: {
            minWidth: '38px',
          },
        },
      },
    },
  });

  const drawer = (
    <Box
      className="theme-container"
      sx={{
        height: '100vh',
        width:
          isCoursePage && !isMobileView && !drawerOpen && !mobileOpen
            ? MINI_DRAWER_WIDTH
            : DRAWER_WIDTH,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(180deg, var(--color-bg) 0%, var(--color-bg) 100%)',
        borderRight: '1px solid var(--color-border)',
        backdropFilter: 'blur(20px)',
        boxSizing: 'border-box',
      }}
      onMouseEnter={() => {
        handleDrawerHover(true);
      }}
      onMouseLeave={() => handleDrawerHover(false)}
    >
      {location.pathname === '/user-profile' ? (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            className="theme-bg"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid var(--color-border)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {side_bar_logo_url ? (
              <img
                style={{
                  width: isCoursePage && !isMobileView ? '32px' : '140px',
                  height: 'auto',
                  transition: 'width 0.3s ease',
                  cursor: 'pointer',
                }}
                src={imagesObj && side_bar_logo_url}
                alt="Optigrit"
                onClick={(e) => handleNavigate(e, '/organization')}
              />
            ) : (
              <img
                style={{
                  width: isCoursePage && !isMobileView ? '32px' : '120px',
                  height: 'auto',
                  transition: 'width 0.3s ease',
                  cursor: 'pointer',
                }}
                src={resolvedTheme === 'dark' ? sideBarLogoDark : sideBarLogo}
                alt="Optigrit"
                onClick={(e) => handleNavigate(e, '/organization')}
              />
            )}
          </Box>
          {tokenData && (
            <>
              <Box sx={{ p: 1, borderBottom: '1px solid var(--color-border)', flex: 1 }}>
                {sidebarUserProfileTop.map((page, index) => (
                  <MenuItem
                    key={index}
                    item={page}
                    selected={selectedPath === page.path}
                    onClick={(e) => handleNavigate(e, page.path)}
                    isCollapsed={false}
                  />
                ))}
              </Box>
              <Box sx={{ p: 1, borderTop: '1px solid var(--color-border)' }}>
                {sidebarUserProfileBottom.map((page, index) => (
                  <MenuItem
                    key={index}
                    item={page}
                    selected={selectedPath === page.path}
                    onClick={(e) => (page.onClick ? page.onClick(e) : handleNavigate(e, page.path))}
                    isCollapsed={false}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid var(--color-border)',
              backdropFilter: 'blur(10px)',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            {side_bar_logo_url ? (
              <img
                style={{
                  width: isCoursePage && !isMobileView && !drawerOpen ? '40px' : '120px',
                  height: 'auto',
                  transition: 'width 0.3s ease',
                  cursor: 'pointer',
                }}
                src={imagesObj && side_bar_logo_url}
                alt="Optigrit"
                onClick={(e) => handleNavigate(e, '/organization')}
              />
            ) : (
              <img
                style={{
                  width: isCoursePage && !isMobileView && !drawerOpen ? '40px' : '120px',
                  height: 'auto',
                  transition: 'width 0.3s ease',
                  cursor: 'pointer',
                }}
                src={isCoursePage && !isMobileView && !drawerOpen ? mini_sideBar_logo : resolvedTheme === 'dark' ? sideBarLogoDark : sideBarLogo}
                alt="Optigrit"
                onClick={(e) => handleNavigate(e, '/organization')}
              />
            )}
          </Box>
          {tokenData && (
            <>
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 2,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(148, 163, 184, 0.3)',
                    borderRadius: '2px',
                    '&:hover': {
                      background: 'rgba(148, 163, 184, 0.5)',
                    },
                  },
                }}
              >
                {renderMenuSections()}
              </Box>

              <Box
                sx={{
                  borderTop: '1px solid var(--color-border)',
                  backdropFilter: 'blur(10px)',
                  p: 0,
                }}
              >
                {pagesBottom.map((page, index) => (
                  <MenuItem
                    key={index}
                    item={page}
                    selected={selectedPath === page.path}
                    onClick={(e) => (page.onClick ? page.onClick(e) : handleNavigate(e, page.path))}
                    isCollapsed={isCoursePage && !isMobileView && !drawerOpen}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return loading ? (
    <Skeletons type="sideBar" />
  ) : (
    <>
      <div className="SideBar theme-container">
        <Box
          sx={{
            display: {
              xs: isChatPage ? 'contents' : 'initial',
            },
          }}
        >
          {isCoursePage && !isMobileView && drawerOpen && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                // backgroundColor: 'rgba(15, 23, 42, 0.3)',
                backdropFilter: 'blur(4px)',
                zIndex: (theme) => theme.zIndex.drawer - 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          )}
          <CssBaseline />
          <AppBar
            position="fixed"
            sx={{
              backgroundColor: 'var(--color-bg-secondary)',
              backdropFilter: 'blur(20px)',
              color: 'var(--color-text)',
              display: {
                sm: showAppBar ? 'flex' : 'none',
                md: showAppBar ? 'flex' : 'none',
              },
              width: {
                sm: showAppBar
                  ? '100%'
                  : `calc(100% - ${
                      isCoursePage && !isMobileView ? MINI_DRAWER_WIDTH : DRAWER_WIDTH
                    }px)`,
                md: showAppBar
                  ? '100%'
                  : `calc(100% - ${
                      isCoursePage && !isMobileView ? MINI_DRAWER_WIDTH : DRAWER_WIDTH
                    }px)`,
              },
              ml: {
                sm: showAppBar
                  ? '0px'
                  : `${isCoursePage && !isMobileView ? MINI_DRAWER_WIDTH : DRAWER_WIDTH}px`,
              },
            }}
          >
            <Toolbar
              sx={{
                // display: {
                //   sm: showAppBar ? 'flex' : 'none',
                //   md: showAppBar ? 'flex' : 'none',
                // },
                justifyContent: 'space-between',
                minHeight: { sm: '64px!important' },
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 2,
                  display: {
                    sm: isContestPage || (isCoursePage && isMobileView) ? 'block' : 'none',
                    md: isContestPage || (isCoursePage && isMobileView) ? 'block' : 'none',
                  },
                  borderRadius: '12px',
                  padding: '8px',
                }}
              >
                <MenuIcon />
              </IconButton>
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                <Link to={`/org/${orgName}/dashboard`}>
                  {home_navbar_phone_url ? (
                    <img
                      style={{ padding: '10px 0px 10px 16px', height: '40px' }}
                      src={imagesObj && home_navbar_phone_url}
                      alt=""
                    />
                  ) : (
                    <img
                      style={{ padding: '10px 0px 3px 16px', height: '40px', width: 'auto' }}
                      src={navbar_logo}
                      alt=""
                    />
                  )}
                </Link>
              </Grid>
            </Toolbar>
          </AppBar>
          <Box
            component="nav"
            sx={{
              width: { sm: isCoursePage && !isMobileView ? MINI_DRAWER_WIDTH : DRAWER_WIDTH },
              flexShrink: { sm: 0 },
            }}
            aria-label="navigation"
          >
            <Toolbar
              sx={{
                display: { md: showAppBar ? 'block' : 'none', sm: showAppBar ? 'block' : 'none' },
              }}
            />
            <ThemeProvider theme={sidebarTheme}>
              <Drawer
                container={container}
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  display: {
                    xs: 'block',
                    sm: showAppBar || (isCoursePage && isMobileView) ? 'block' : 'none',
                    md: showAppBar || (isCoursePage && isMobileView) ? 'block' : 'none',
                  },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: DRAWER_WIDTH,
                    zIndex: (theme) => theme.zIndex.drawer,
                    border: 'none',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {drawer}
              </Drawer>
              <Drawer
                variant="permanent"
                sx={{
                  display: {
                    xs: 'none',
                    sm: showAppBar || (isCoursePage && isMobileView) ? 'none' : 'block',
                    lg: 'block',
                  },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width:
                      isCoursePage && !isMobileView && !mobileOpen ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: 'none',
                    boxShadow: '0 0 40px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      width: DRAWER_WIDTH,
                    },
                    zIndex: (theme) => theme.zIndex.drawer,
                  },
                }}
                open
              >
                {drawer}
              </Drawer>
            </ThemeProvider>
          </Box>
        </Box>
      </div>
      <Menu
        anchorEl={themeAnchor}
        open={Boolean(themeAnchor)}
        onClose={() => setThemeAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        {[
          { value: 'light', label: 'Light', icon: LightModeOutlinedIcon },
          { value: 'dark', label: 'Dark', icon: DarkModeOutlinedIcon },
          { value: 'system', label: 'System', icon: SettingsBrightnessOutlinedIcon }
        ].map((option) => (
          <MuiMenuItem key={option.value} onClick={() => { setTheme(option.value); setThemeAnchor(null); }} selected={theme === option.value} sx={{ '&.Mui-selected': { backgroundColor: 'var(--color-primary)', color: 'white', '&:hover': { backgroundColor: 'var(--color-primary)' } } }}>
            <ListItemIcon>
              <option.icon fontSize="small" sx={{ color: theme === option.value ? 'white' : 'inherit' }} />
            </ListItemIcon>
            <Typography sx={{ fontSize: '14px', color: theme === option.value && 'white' }}>{option.label}</Typography>
          </MuiMenuItem>
        ))}
      </Menu>
      {notificationsPopup.open && (
        <NotificationsPopup
          open={notificationsPopup.open}
          anchorEl={notificationsPopup.anchorEl}
          placement={notificationsPopup.placement}
          handleClose={closeNotificationsPopup}
          setSelectedOrgName={props.setSelectedOrgName}
        />
      )}
    </>
  );
}

SideBarResponsive.propTypes = {
  window: PropTypes.func,
  setMobileOpen: PropTypes.func,
  setSelectedOrgName: PropTypes.func,
  show: PropTypes.bool,
};

export default SideBarResponsive;
